import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../../hooks/useToast'
import type {
  AccessListRequest,
  AccessListSuccessResponse,
  AccessListErrorResponse,
  UpdateAccessListRequest,
  UpdateAccessListSuccessResponse,
  UpdateAccessListErrorResponse,
  ShareModalState,
  ViewableParentSuccessResponse,
} from '../../../../interfaces'
import { MLContext, UserContext } from '../../../../providers'
import { ACLEntityTypeEnum } from '../../../../constants'
import ShareModal, { UserGroupLabel, UserLabel } from './ShareModal'
import { isRestrictedUser } from '../../../../../utils/roles'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import { Base64 } from 'js-base64'

interface Props {
  assetId: string
  assetName: string
  assetType: string
  isOpen: boolean
  onClose(): void
  getViewableParent?(): Promise<ViewableParentSuccessResponse>
}

const ShareAssetModal = ({
  assetId,
  assetName,
  assetType,
  isOpen = false,
  onClose,
  getViewableParent,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const [
    {
      selectedBreadcrumbFolder,
      viewableParent,
      mediaCollectionRoute,
      currentMediaCollection,
      refetch,
    },
  ] = useContext(MLContext)

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getAssetAccessList = async (): Promise<ShareModalState | undefined> => {
    const res = (await request.post(
      `/api/clients/${user?.clientNumber}/access-control/assets/build-access-list`,
      {
        assetIds: [assetId],
      } as AccessListRequest,
    )) as AccessListSuccessResponse | AccessListErrorResponse
    if (request.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      const successfulResponse = res as AccessListSuccessResponse
      const accessList = successfulResponse.acls[assetId].acl
        .filter(
          aclItem => aclItem.entity.type !== ACLEntityTypeEnum.MEDIA_COLLECTION,
        )
        .map(aclItem => {
          if (aclItem.entity.type === ACLEntityTypeEnum.USER) {
            const foundUser = successfulResponse.users.find(
              userItem => userItem.userId === aclItem.entity.id,
            )
            return {
              label: (
                <UserLabel
                  userName={foundUser?.userName ?? ''}
                  userEmail={foundUser?.userEmail ?? ''}
                />
              ),
              extraInfo: foundUser,
              value: aclItem,
            }
          }
          const foundGroup = successfulResponse.userGroups.find(
            userGroup => userGroup.groupId === aclItem.entity.id,
          )
          return {
            label: (
              <UserGroupLabel
                groupLabel={foundGroup?.label ?? ''}
                members={foundGroup?.userIds.length ?? 0}
              />
            ),
            extraInfo: foundGroup,
            value: aclItem,
          }
        })
      const users = successfulResponse.users
        .filter(
          userItem =>
            !(
              successfulResponse.acls[assetId].createdBy?.userId ===
              userItem.userId
            ) &&
            !successfulResponse.acls[assetId].acl.find(
              alreadyExistingAclItem =>
                alreadyExistingAclItem.entity.id === userItem.userId &&
                alreadyExistingAclItem.entity.type === ACLEntityTypeEnum.USER,
            ),
        )
        .map(userItem => ({
          label: (
            <UserLabel
              userName={userItem.userName}
              userEmail={userItem.userEmail}
            />
          ),
          extraInfo: userItem,
          value: {
            entity: { id: userItem.userId, type: ACLEntityTypeEnum.USER },
          },
        }))
      const userGroups = successfulResponse.userGroups
        .filter(
          userGroupItem =>
            !successfulResponse.acls[assetId].acl.find(
              alreadyExistingAclItem =>
                alreadyExistingAclItem.entity.id === userGroupItem.groupId &&
                alreadyExistingAclItem.entity.type ===
                  ACLEntityTypeEnum.USER_GROUP,
            ),
        )
        .map(userGroupItem => ({
          label: (
            <UserGroupLabel
              groupLabel={userGroupItem.label}
              members={userGroupItem.userIds.length}
            />
          ),
          extraInfo: userGroupItem,
          value: {
            entity: {
              id: userGroupItem.groupId,
              type: ACLEntityTypeEnum.USER_GROUP,
            },
          },
        }))
      const createdBy = successfulResponse.acls[assetId].createdBy
      const mediaCollections = successfulResponse.acls[assetId].acl
        .filter(
          aclItem =>
            aclItem.entity.type === ACLEntityTypeEnum.MEDIA_COLLECTION &&
            successfulResponse.mediaCollections.find(
              mediaCollection =>
                mediaCollection.mediaCollectionId === aclItem.entity.id,
            ),
        )
        .map(aclItem => {
          const foundMediaCollection = successfulResponse.mediaCollections.find(
            mediaCollection =>
              mediaCollection.mediaCollectionId === aclItem.entity.id,
          )
          let mediaCollectionAclItems
          if (foundMediaCollection && foundMediaCollection.acl) {
            mediaCollectionAclItems = foundMediaCollection.acl.map(
              mediaCollectionAclItem => {
                if (
                  mediaCollectionAclItem.entity.type === ACLEntityTypeEnum.USER
                ) {
                  const foundUser = successfulResponse.users.find(
                    userItem =>
                      userItem.userId === mediaCollectionAclItem.entity.id,
                  )
                  return {
                    label: (
                      <UserLabel
                        userName={foundUser?.userName ?? ''}
                        userEmail={foundUser?.userEmail ?? ''}
                      />
                    ),
                    value: mediaCollectionAclItem,
                  }
                }
                const foundGroup = successfulResponse.userGroups.find(
                  group => group.groupId === mediaCollectionAclItem.entity.id,
                )
                return {
                  label: (
                    <UserGroupLabel
                      groupLabel={foundGroup?.label ?? ''}
                      members={foundGroup?.userIds.length ?? 0}
                    />
                  ),
                  value: mediaCollectionAclItem,
                }
              },
            )
          }

          return {
            extraInfo: foundMediaCollection
              ? { ...foundMediaCollection, aclLabels: mediaCollectionAclItems }
              : undefined,
            value: aclItem,
          }
        })
      return {
        accessList,
        users,
        userGroups,
        createdBy,
        mediaCollections,
      }
    }
  }

  const updateAssetAccessList = async (
    requestBody: UpdateAccessListRequest,
  ): Promise<void> => {
    const res = (await request.patch(
      `api/clients/${user?.clientNumber}/access-control/assets/${assetId}`,
      requestBody,
    )) as UpdateAccessListSuccessResponse | UpdateAccessListErrorResponse
    if (request.error) {
      // do error handling
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }
    if (
      (
        requestBody?.acl?.remove.filter(obj => obj?.entity?.id === user?.id) ||
        []
      ).length > 0 &&
      selectedBreadcrumbFolder &&
      isRestrictedUser(user?.roles)
    ) {
      let folderPath = selectedBreadcrumbFolder?.folderPath as string
      let viewableFolderPath = folderPath
      let unViewableBasePath = ''
      let newFolderPath = ''

      if (
        viewableParent &&
        !_.isUndefined((viewableParent as ViewableParentSuccessResponse).asset)
      ) {
        const index = (
          viewableParent as ViewableParentSuccessResponse
        ).asset?.assetPath.lastIndexOf('/')
        if (index) {
          viewableFolderPath = folderPath.slice(index, folderPath.length)
          unViewableBasePath = folderPath.slice(0, index)
        }
      }

      const folderPathArray = viewableFolderPath.split('/')
      if (
        folderPathArray[folderPathArray.length - 1] ===
        selectedBreadcrumbFolder?.name
      ) {
        folderPathArray.pop()
      }
      viewableFolderPath = folderPathArray.join('/')

      if (viewableFolderPath && viewableFolderPath !== '/') {
        newFolderPath = `${unViewableBasePath}${viewableFolderPath}`
      }

      mediaCollectionRoute
        ? history.replace(
            `/media-collections/${currentMediaCollection}/${Base64.encode(
              newFolderPath,
              true,
            )}${queryFromUrl as string}`,
          )
        : history.replace(
            `/media-library/${Base64.encode(newFolderPath, true)}${
              queryFromUrl as string
            }`,
          )
    } else {
      // no-op
    }

    toast({
      description: t('successfully_updated', 'Successfully updated'),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
  }

  return (
    <ShareModal
      assetName={assetName}
      assetType={assetType}
      isOpen={isOpen}
      onClose={onClose}
      getAccessList={getAssetAccessList}
      updateAccessList={updateAssetAccessList}
      loadingAccessList={loading}
      getViewableParent={getViewableParent}
    />
  )
}

export default ShareAssetModal
